// M6 — Supabase-compat shim.
// Replaces the real @supabase/supabase-js client with a drop-in object
// that routes all calls through the labourcore-api (localhost:3010).
// ~150 files import `{ supabase }` from this module — keeping the same
// export shape means zero import changes in feature files.

import {
  getAccessToken, getRefreshToken, setTokens, clearTokens,
  authedFetch, apiPost, ApiError, BASE_URL, fetchJsonSafe,
} from "./client";

/* ── Cached user / session ─────────────────────────────────────── */

interface CachedUser {
  id: string;
  email: string;
  user_metadata: Record<string, unknown>;
  [key: string]: unknown;
}

interface CachedSession {
  access_token: string;
  refresh_token: string;
  user: CachedUser;
}

let _cachedUser: CachedUser | null = null;
let _cachedSession: CachedSession | null = null;

function _hydrateSessionFromStorage(): CachedSession | null {
  const at = getAccessToken();
  const rt = getRefreshToken();
  if (!at || !rt) return null;
  try {
    // Decode JWT payload for user info
    const payload = JSON.parse(atob(at.split(".")[1]!));
    _cachedUser = {
      id: payload.sub,
      email: payload.email || "",
      user_metadata: {},
    };
    _cachedSession = { access_token: at, refresh_token: rt, user: _cachedUser };
    return _cachedSession;
  } catch {
    return null;
  }
}

// Hydrate on module load
_hydrateSessionFromStorage();

export function _setCachedUser(user: CachedUser | null, session: CachedSession | null): void {
  _cachedUser = user;
  _cachedSession = session;
}

export function _getCachedUser(): CachedUser | null { return _cachedUser; }

/* ── Auth state change listeners ───────────────────────────────── */

type AuthHandler = (event: string, session: CachedSession | null) => void;
const _authListeners = new Set<AuthHandler>();

function _notifyAuthChange(event: string, session: CachedSession | null): void {
  _authListeners.forEach(fn => {
    try { fn(event, session); } catch (e) { console.warn("[auth-shim] listener error:", e); }
  });
  window.dispatchEvent(new Event("lc:auth-changed"));
  // Also fire legacy event for helpers.ts
  window.dispatchEvent(new Event("lc-auth-changed"));
}

/* ── Error message mapping ─────────────────────────────────────── */

/* labourcore-api's error envelope is either {error: "human message"}
   (AppError, e.g. "Invalid email or password") or, for a request that
   fails schema validation before it even reaches the handler,
   {error: "validation_error", details: [{path, message, code}, ...]}.
   That second shape's `error` field is a machine code, not copy — passing
   it straight to the UI is what produced the raw "validation_error" bug.
   Prefer the first validation detail's message (already human-readable,
   e.g. "Password must be at least 8 characters") over the code. */
export function friendlyErrorMessage(json: unknown, fallback: string): string {
  const body = json as { error?: string; details?: Array<{ message?: string }> } | null;
  if (body?.error === "validation_error") {
    return body.details?.[0]?.message || "Please check the highlighted fields and try again.";
  }
  return body?.error || fallback;
}

/* ── Auth surface (mirrors supabase.auth.*) ────────────────────── */

const authShim = {
  async getSession(): Promise<{ data: { session: CachedSession | null } }> {
    if (!_cachedSession) _hydrateSessionFromStorage();
    return { data: { session: _cachedSession } };
  },

  async getUser(): Promise<{ data: { user: CachedUser | null }; error: null }> {
    if (!_cachedUser) _hydrateSessionFromStorage();
    return { data: { user: _cachedUser }, error: null };
  },

  async signInWithPassword(args: { email: string; password: string }): Promise<{
    data: { user: CachedUser | null; session: CachedSession | null } | null;
    error: { message: string } | null;
  }> {
    const { ok, json, transportError } = await fetchJsonSafe<any>(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: args.email, password: args.password }),
    });

    // Network failure or a non-JSON body (e.g. the dev API restarting
    // mid-request) — never surface the raw fetch/parse error to the UI.
    if (transportError) return { data: null, error: { message: transportError } };

    if (!ok) {
      return { data: null, error: { message: friendlyErrorMessage(json, "Sign-in failed. Please try again.") } };
    }

    try {
      // Handle reset_required
      if (json?.status === "reset_required" && json?.user?.id) {
        // Store the reset token and user info so AuthContext can handle it
        const user: CachedUser = {
          id: json.user.id,
          email: json.user.email,
          user_metadata: {},
        };
        // Signal reset_required by returning an error with a special message
        // that AuthContext can detect + the reset_token attached to the user
        (user as any).__reset_token = json.reset_token;
        (user as any).__reset_required = true;
        return {
          data: { user, session: null },
          error: { message: "PASSWORD_RESET_REQUIRED" },
        };
      }

      // Normal login: {access_token, refresh_token, user}
      if (json?.access_token && json?.refresh_token && json?.user?.id) {
        setTokens(json.access_token, json.refresh_token);
        const user: CachedUser = {
          id: json.user.id,
          email: json.user.email,
          user_metadata: {},
        };
        const session: CachedSession = {
          access_token: json.access_token,
          refresh_token: json.refresh_token,
          user,
        };
        _cachedUser = user;
        _cachedSession = session;
        _notifyAuthChange("SIGNED_IN", session);
        return { data: { user, session }, error: null };
      }

      return { data: null, error: { message: "Sign-in failed. Please try again." } };
    } catch {
      // Response was valid JSON but not in the expected shape.
      return { data: null, error: { message: "Sign-in failed. Please try again." } };
    }
  },

  async signUp(args: {
    email: string;
    password: string;
    options?: { data?: Record<string, unknown> };
  }): Promise<{
    data: { user: CachedUser | null; session: CachedSession | null } | null;
    error: { message: string } | null;
  }> {
    const { ok, json, transportError } = await fetchJsonSafe<any>(`${BASE_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: args.email,
        password: args.password,
        name: args.options?.data?.name || args.email,
        role: args.options?.data?.role || "coordinator",
      }),
    });

    if (transportError) return { data: null, error: { message: transportError } };

    if (!ok) {
      return { data: null, error: { message: friendlyErrorMessage(json, "Registration failed. Please try again.") } };
    }

    try {
      if (json?.access_token && json?.refresh_token) {
        setTokens(json.access_token, json.refresh_token);
        const user: CachedUser = {
          id: json.user?.id || "",
          email: args.email,
          user_metadata: args.options?.data || {},
        };
        const session: CachedSession = {
          access_token: json.access_token,
          refresh_token: json.refresh_token,
          user,
        };
        _cachedUser = user;
        _cachedSession = session;
        _notifyAuthChange("SIGNED_IN", session);
        return { data: { user, session }, error: null };
      }
      return { data: null, error: { message: "Registration failed. Please try again." } };
    } catch {
      return { data: null, error: { message: "Registration failed. Please try again." } };
    }
  },

  async signOut(): Promise<void> {
    try {
      const at = getAccessToken();
      if (at) {
        await fetch(`${BASE_URL}/auth/logout`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${at}` },
        }).catch(() => {});
      }
    } finally {
      clearTokens();
      _cachedUser = null;
      _cachedSession = null;
      _notifyAuthChange("SIGNED_OUT", null);
    }
  },

  onAuthStateChange(handler: AuthHandler): { data: { subscription: { unsubscribe: () => void } } } {
    _authListeners.add(handler);
    // Fire initial event if we have a session
    if (_cachedSession) {
      setTimeout(() => handler("INITIAL_SESSION", _cachedSession), 0);
    }
    return {
      data: {
        subscription: {
          unsubscribe: () => { _authListeners.delete(handler); },
        },
      },
    };
  },

  /* MFA stubs — not implemented in labourcore-api yet */
  mfa: {
    async listFactors() { return { data: { totp: [], phone: [] }, error: null }; },
    async getAuthenticatorAssuranceLevel() { return { data: { currentLevel: null, nextLevel: null }, error: null }; },
    async enroll(_args: any) { return { data: null, error: { message: "MFA not implemented" } }; },
    async challenge(_args: any) { return { data: null, error: { message: "MFA not implemented" } }; },
    async verify(_args: any) { return { data: null, error: { message: "MFA not implemented" } }; },
    async challengeAndVerify(_args: any) { return { data: null, error: { message: "MFA not implemented" } }; },
    async unenroll(_args: any) { return { data: null, error: { message: "MFA not implemented" } }; },
  },
};

/* ── RPC surface (supabase.rpc) ────────────────────────────────── */

/** The /rpc/:name route runs `SELECT * FROM fn()`, which for a
 *  scalar-jsonb-returning function wraps the result as
 *  [{ <fn_name>: <value> }] rather than returning the value directly.
 *  Table/SETOF-returning RPCs are unaffected (each row already has real
 *  columns) — only call this for RPCs known to return a single scalar. */
export function unwrapRpcScalar<T>(name: string, data: unknown): T | null {
  if (data == null) return null;
  const row = Array.isArray(data) ? data[0] : data;
  if (row && typeof row === "object" && name in (row as object)) {
    return (row as Record<string, unknown>)[name] as T;
  }
  return (row as T) ?? null;
}

async function rpcShim(name: string, args?: Record<string, unknown>): Promise<{ data: unknown; error: { message: string; code?: string } | null }> {
  try {
    const result = await apiPost<{ data: unknown }>(`/rpc/${name}`, { args: args ?? {} });
    return { data: result.data, error: null };
  } catch (e) {
    if (e instanceof ApiError) {
      return { data: null, error: { message: e.message, code: e.code } };
    }
    return { data: null, error: { message: (e as Error).message } };
  }
}

/* ── Query builder surface (supabase.from) ─────────────────────── */

// Tables that have REST endpoints in the API
const REST_TABLES = new Set([
  "compliance_documents", "compliance-documents",
  "fee_events", "fee-events",
  "platform_invoices", "platform-invoices",
  "invoices",
  "companies",
]);

function fromShim(tableName: string) {
  const filters: Record<string, string> = {};
  const eqFilters: Array<[string, unknown]> = [];
  const isFilters: Array<[string, unknown]> = [];
  let _select = "*";
  let _limit = 500;
  let _offset = 0;
  let _orderCol: string | null = null;
  let _orderAsc = false;
  let _operation: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  let _insertData: unknown = null;
  let _updateData: unknown = null;
  let _upsertData: unknown = null;
  let _upsertOpts: { onConflict?: string } = {};
  let _wantSingle = false;
  let _wantMaybeSingle = false;
  let _wantCount = false;

  const builder: any = {
    select(columns?: string, opts?: { count?: string; head?: boolean }) {
      /* .update()/.insert()/.upsert()/.delete() followed by .select() is
         the standard Supabase idiom for "return these columns from the
         mutated rows" (Postgres RETURNING) — it does NOT mean "switch to
         a select query". Only default to "select" when no mutating
         method has set the operation yet, otherwise this silently turns
         every .update(...).eq(...).select() call into a no-op read that
         reports success without ever writing anything. */
      if (_operation !== "update" && _operation !== "insert" && _operation !== "upsert" && _operation !== "delete") {
        _operation = "select";
      }
      if (columns) _select = columns;
      if (opts?.count) _wantCount = true;
      return builder;
    },
    insert(data: unknown) {
      _operation = "insert";
      _insertData = data;
      return builder;
    },
    update(data: unknown) {
      _operation = "update";
      _updateData = data;
      return builder;
    },
    upsert(data: unknown, opts?: { onConflict?: string }) {
      _operation = "upsert";
      _upsertData = data;
      if (opts) _upsertOpts = opts;
      return builder;
    },
    delete() {
      _operation = "delete";
      return builder;
    },
    eq(col: string, val: unknown) {
      eqFilters.push([col, val]);
      filters[col] = String(val);
      return builder;
    },
    neq(col: string, val: unknown) {
      filters[`${col}__neq`] = String(val);
      return builder;
    },
    in(col: string, vals: unknown[]) {
      filters[`${col}__in`] = vals.map(String).join(",");
      return builder;
    },
    is(col: string, val: unknown) {
      isFilters.push([col, val]);
      if (val === null) filters[col] = "null";
      return builder;
    },
    gt(col: string, val: unknown) { filters[`${col}__gt`] = String(val); return builder; },
    gte(col: string, val: unknown) { filters[`${col}__gte`] = String(val); return builder; },
    lt(col: string, val: unknown) { filters[`${col}__lt`] = String(val); return builder; },
    lte(col: string, val: unknown) { filters[`${col}__lte`] = String(val); return builder; },
    like(col: string, val: string) { filters[`${col}__like`] = val; return builder; },
    ilike(col: string, val: string) { filters[`${col}__ilike`] = val; return builder; },
    contains(col: string, val: unknown) { filters[`${col}__contains`] = JSON.stringify(val); return builder; },
    containedBy(col: string, val: unknown) { filters[`${col}__containedBy`] = JSON.stringify(val); return builder; },
    overlaps(col: string, val: unknown) { filters[`${col}__overlaps`] = JSON.stringify(val); return builder; },
    not(col: string, op: string, val: unknown) {
      filters[`${col}__not_${op}`] = String(val);
      return builder;
    },
    or(clause: string) {
      filters["__or"] = clause;
      return builder;
    },
    limit(n: number) { _limit = n; return builder; },
    offset(n: number) { _offset = n; return builder; },
    range(from: number, to: number) { _offset = from; _limit = to - from + 1; return builder; },
    order(col: string, opts?: { ascending?: boolean }) {
      _orderCol = col;
      _orderAsc = opts?.ascending !== false;
      return builder;
    },
    single() { _wantSingle = true; _limit = 1; return builder; },
    maybeSingle() { _wantMaybeSingle = true; _limit = 1; return builder; },

    /* Execute the built query via RPC proxy.
       All table ops go through supabase.rpc("__table_proxy", {...}) which
       doesn't exist on the API. Instead, we route through the REST
       endpoints for supported tables, and use RPC for everything else. */
    async _exec(): Promise<{ data: unknown; error: { message: string; code?: string; hint?: string; details?: string } | null; count?: number }> {
      try {
        const apiTableName = tableName.replace(/_/g, "-");

        // For SELECT on REST-exposed tables
        if (_operation === "select" && REST_TABLES.has(tableName)) {
          const params: Record<string, string> = {
            limit: String(_limit),
            offset: String(_offset),
          };
          // Add eq filters
          for (const [k, v] of eqFilters) {
            params[k] = String(v);
          }
          if (_orderCol) {
            params["sort"] = `${_orderCol}.${_orderAsc ? "asc" : "desc"}`;
          }
          const sp = new URLSearchParams(params);
          const res = await authedFetch(`${BASE_URL}/api/${apiTableName}?${sp}`);
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            return { data: null, error: { message: body.error || `HTTP ${res.status}` } };
          }
          const json = await res.json();
          let data = json.data || [];
          if (_wantSingle) {
            if (!data[0]) return { data: null, error: { message: "Not found" } };
            return { data: data[0], error: null };
          }
          if (_wantMaybeSingle) return { data: data[0] ?? null, error: null };
          if (_wantCount) return { data, error: null, count: json.count ?? data.length };
          return { data, error: null };
        }

        // For PATCH on REST-exposed tables
        if (_operation === "update" && REST_TABLES.has(tableName)) {
          const idFilter = eqFilters.find(([k]) => k === "id");
          if (idFilter) {
            const res = await authedFetch(`${BASE_URL}/api/${apiTableName}/${idFilter[1]}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(_updateData),
            });
            if (!res.ok) {
              const body = await res.json().catch(() => ({}));
              return { data: null, error: { message: body.error || `HTTP ${res.status}` } };
            }
            const json = await res.json();
            return { data: json.data || _updateData, error: null };
          }
        }

        // For all other operations: route through __table_proxy RPC
        // This is a catch-all that goes through the RPC endpoint
        const rpcArgs: Record<string, unknown> = {
          table_name: tableName,
          operation: _operation,
          filters: Object.fromEntries(eqFilters),
          is_filters: Object.fromEntries(isFilters),
          select_columns: _select,
          limit: _limit,
          offset: _offset,
          order_col: _orderCol,
          order_asc: _orderAsc,
        };
        if (_insertData) rpcArgs.row_data = _insertData;
        if (_updateData) rpcArgs.row_data = _updateData;
        if (_upsertData) {
          rpcArgs.row_data = _upsertData;
          rpcArgs.upsert_opts = _upsertOpts;
        }

        const result = await apiPost<{ data: unknown }>(`/rpc/__table_proxy`, { args: rpcArgs });
        let data = result.data;
        if (_wantSingle) {
          const arr = Array.isArray(data) ? data : [data];
          if (!arr[0]) return { data: null, error: { message: "Not found" } };
          return { data: arr[0], error: null };
        }
        if (_wantMaybeSingle) {
          const arr = Array.isArray(data) ? data : [data];
          return { data: arr[0] ?? null, error: null };
        }
        return { data, error: null };
      } catch (e) {
        if (e instanceof ApiError) {
          return { data: null, error: { message: e.message, code: e.code } };
        }
        return { data: null, error: { message: (e as Error).message } };
      }
    },

    then(resolve: (v: any) => void, reject?: (e: any) => void) {
      return builder._exec().then(resolve, reject);
    },
  };

  return builder;
}

/* ── Storage surface (M7 — wired to /storage endpoints) ────────── */

function storageShim() {
  const bucket = (name: string) => ({
    /**
     * Upload a file. Gets a presigned PUT URL from the API, then PUTs directly.
     * Matches supabase-js: returns { data: { path }, error: null } on success.
     */
    upload: async (path: string, file: Blob | File, opts?: { contentType?: string; upsert?: boolean; cacheControl?: string }) => {
      try {
        const contentType = opts?.contentType || (file as File).type || "application/octet-stream";
        const res = await authedFetch(`${BASE_URL}/storage/${encodeURIComponent(name)}/upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path, contentType }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        const { signedUrl } = await res.json();
        // PUT directly to SAS/presigned URL (auth is in the query string).
        // Azure Blob BlockBlob PUTs require x-ms-blob-type; harmless for MinIO/S3.
        const putRes = await fetch(signedUrl, {
          method: "PUT",
          headers: {
            "Content-Type": contentType,
            "x-ms-blob-type": "BlockBlob",
          },
          body: file,
        });
        if (!putRes.ok) {
          return { data: null, error: { message: `Upload PUT failed: ${putRes.status}` } };
        }
        return { data: { path }, error: null };
      } catch (e: any) {
        return { data: null, error: { message: e.message || "Upload failed" } };
      }
    },

    /**
     * Download a file. Gets a presigned GET URL, fetches it, returns a Blob.
     * Matches supabase-js: returns { data: Blob, error: null } on success.
     */
    download: async (path: string) => {
      try {
        const res = await authedFetch(`${BASE_URL}/storage/${encodeURIComponent(name)}/download-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        const { signedUrl } = await res.json();
        const dlRes = await fetch(signedUrl);
        if (!dlRes.ok) {
          return { data: null, error: { message: `Download failed: ${dlRes.status}` } };
        }
        const blob = await dlRes.blob();
        return { data: blob, error: null };
      } catch (e: any) {
        return { data: null, error: { message: e.message || "Download failed" } };
      }
    },

    /**
     * Remove objects by path array.
     * Matches supabase-js: returns { data: null, error: null } on success.
     */
    remove: async (paths: string[]) => {
      try {
        const res = await authedFetch(`${BASE_URL}/storage/${encodeURIComponent(name)}/object`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paths }),
        });
        if (!res.ok && res.status !== 204) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        return { data: null, error: null };
      } catch (e: any) {
        return { data: null, error: { message: e.message || "Remove failed" } };
      }
    },

    /**
     * Get a "public" URL. Since R2 doesn't have Supabase-style public URLs,
     * we return a signed download URL. Callers treat this as a display URL.
     * Note: this is synchronous in supabase-js, but our version is too —
     * it returns a placeholder that the caller should replace with a
     * createSignedUrl call for actual access. Kept for API compat.
     */
    getPublicUrl: (path: string) => {
      // Absolute same-origin URL for <img src> / avatar_url persistence.
      // Path segments are encoded individually so nested keys work.
      const cleaned = String(path || "").replace(/^\/+/, "");
      const encodedPath = cleaned
        .split("/")
        .filter(Boolean)
        .map((seg) => encodeURIComponent(seg))
        .join("/");
      const origin =
        typeof window !== "undefined" && window.location?.origin
          ? window.location.origin
          : "";
      const base = BASE_URL || origin;
      return {
        data: {
          publicUrl: `${base}/storage/${encodeURIComponent(name)}/public/${encodedPath}`,
        },
      };
    },

    /**
     * Create a signed download URL.
     * Matches supabase-js: returns { data: { signedUrl }, error: null }.
     */
    createSignedUrl: async (path: string, expiresIn: number) => {
      try {
        const res = await authedFetch(`${BASE_URL}/storage/${encodeURIComponent(name)}/download-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path, expiresIn }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        const { signedUrl } = await res.json();
        return { data: { signedUrl }, error: null };
      } catch (e: any) {
        return { data: null, error: { message: e.message || "createSignedUrl failed" } };
      }
    },

    /**
     * List objects in a bucket with optional prefix.
     * Matches supabase-js list shape.
     */
    list: async (prefix?: string, opts?: { limit?: number }) => {
      try {
        const res = await authedFetch(`${BASE_URL}/storage/${encodeURIComponent(name)}/list`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prefix, limit: opts?.limit }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: res.statusText }));
          return { data: null, error: { message: err.error || res.statusText } };
        }
        const data = await res.json();
        return { data, error: null };
      } catch (e: any) {
        return { data: null, error: { message: e.message || "List failed" } };
      }
    },

    /**
     * getSignedUrl — alias for createSignedUrl (supabase-js compat).
     */
    getSignedUrl: async (path: string, expiresIn: number) => {
      // Delegate to createSignedUrl — same API shape
      return bucket(name).createSignedUrl(path, expiresIn);
    },
  });
  return { from: bucket };
}

/* ── Realtime surface (no-op stubs) ────────────────────────────── */

function channelShim(name: string) {
  console.warn(`[M6-realtime-stub] channel("${name}") created — realtime not available via labourcore-api`);
  const ch: any = {
    on: (_event: string, _config: any, _handler: any) => ch,
    subscribe: () => ch,
    unsubscribe: () => {},
  };
  return ch;
}

/* ── The main supabase compat object ───────────────────────────── */

export const supabase = {
  auth: authShim,
  rpc: rpcShim,
  from: fromShim,
  storage: storageShim(),
  channel: channelShim,
  removeChannel: (channel: any) => {
    console.warn("[M6-realtime-stub] removeChannel called");
    try { channel?.unsubscribe?.(); } catch { /* ignore */ }
  },
} as any;

export const SUPABASE_READY = true;

/* Legacy window globals for diagnostic scripts */
if (typeof window !== "undefined") {
  (window as any).__SUPABASE_CLIENT = supabase;
  (window as any).__SUPABASE_READY = true;
}
